import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '@/services/api';
import api from '@/services/api';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { 
    Users, 
    Search, 
    Download,
    RefreshCw,
    User,
    Building,
    Mail,
    Phone,
    Calendar,
    Star,
    CheckCircle,
    XCircle,
    AlertCircle,
    Package,
    Coins,
    FileText,
    Eye,
    Edit,
    Trash2,
    ChevronRight,
    Loader2,
    ArrowLeft
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { adminSidebarLinks } from '@/config/adminSidebarLinks';
import { toast } from '@/components/ui/use-toast';
import RequestManagement from '@/components/admin/clients/RequestManagement';
import BookingManagement from '@/components/admin/clients/BookingManagement';
import PaymentManagement from '@/components/admin/clients/PaymentManagement';
import RatingManagement from '@/components/admin/clients/RatingManagement';
import LogiConnectIdBadge from '@/components/ui/logiconnect-id-badge';

const AdminClients = () => {
    const navigate = useNavigate();
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedClient, setSelectedClient] = useState(null);
    const [clientDetails, setClientDetails] = useState(null);
    const [clientDetailsLoading, setClientDetailsLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('details');
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [typeFilter, setTypeFilter] = useState('all');
    const [deleteDialog, setDeleteDialog] = useState({ open: false, client: null });
    const [showMobileList, setShowMobileList] = useState(true); // Pour mobile : true = liste, false = détails

    // Fonction helper pour obtenir l'ID du profil client
    const getClientProfileId = (client) => {
        if (!client) return null;
        // Essayer plusieurs chemins possibles pour l'ID du profil
        return client.profile_data?.id || 
               client.profile?.id || 
               client.client_profile?.id ||
               client.id; // Fallback sur l'ID utilisateur si pas de profil
    };

    // Fonctions helper pour accéder aux données utilisateur et profil de manière flexible
    const getUserData = (clientDetails) => {
        if (!clientDetails) return null;
        // Essayer plusieurs chemins possibles
        return clientDetails.user || 
               clientDetails.user_detail ||
               clientDetails; // Si clientDetails est directement l'utilisateur
    };

    const getProfileData = (clientDetails) => {
        if (!clientDetails) return null;
        // Essayer plusieurs chemins possibles
        return clientDetails.profile || 
               clientDetails.profile_data ||
               clientDetails.client_profile ||
               clientDetails.profile_detail ||
               {}; // Retourner un objet vide si aucun profil trouvé
    };

    // Charger les clients depuis le backend
    useEffect(() => {
        const fetchClients = async () => {
            try {
                setLoading(true);
                // Utiliser l'endpoint spécifique pour les clients
                const response = await adminAPI.getClients({});
                // Formater les données pour correspondre à la structure attendue
                const clientsData = (response.data.results || response.data || []).map(client => ({
                    ...client,
                    requests_count: client.profile_data?._requests_count || client.requests_count || 0,
                    completed_requests: client.profile_data?._completed_requests || 0,
                    total_spent: client.profile_data?._total_spent || client.profile_data?.total_spent || 0,
                    profile_data: {
                        ...client.profile_data,
                        total_spent: client.profile_data?._total_spent || client.profile_data?.total_spent || 0,
                    }
                }));
                
                // Vérifier que les données sont valides
                if (!Array.isArray(clientsData)) {
                    console.error('Format de données invalide:', response.data);
                    toast({
                        title: "Erreur",
                        description: "Format de données invalide reçu du serveur.",
                        variant: "destructive"
                    });
                    return;
                }

                setClients(clientsData);
                
                // Sélectionner le premier client par défaut si disponible
                if (clientsData.length > 0) {
                    const firstClient = clientsData[0];
                    // Vérifier que le client a bien un ID de profil
                    const profileId = getClientProfileId(firstClient);
                    if (profileId) {
                        setSelectedClient(firstClient);
                    } else {
                        console.warn('Le premier client n\'a pas d\'ID de profil valide:', firstClient);
                    }
                }
            } catch (error) {
                console.error('Erreur lors du chargement des clients:', error);
                // En cas d'erreur, essayer avec l'endpoint standard
                try {
                    const fallbackResponse = await api.get('/accounts/admin/users/', { params: { role: 'CLIENT' } });
                    const clientsData = (fallbackResponse.data.results || fallbackResponse.data || []).map(client => ({
                        ...client,
                        requests_count: 0,
                        completed_requests: 0,
                        total_spent: 0,
                    }));
                    setClients(clientsData);
                    if (clientsData.length > 0) {
                        const firstClient = clientsData[0];
                        const profileId = getClientProfileId(firstClient);
                        if (profileId) {
                            setSelectedClient(firstClient);
                        }
                    }
                } catch (fallbackError) {
                    toast({
                        title: "Erreur",
                        description: error.response?.data?.detail || "Impossible de charger les clients.",
                        variant: "destructive"
                    });
                    setClients([]);
                }
            } finally {
                setLoading(false);
            }
        };

        fetchClients();
    }, []);

    // Charger les détails du client sélectionné
    useEffect(() => {
        const fetchClientDetails = async () => {
            if (!selectedClient) {
                setClientDetails(null);
                return;
            }

            try {
                setClientDetailsLoading(true);
                const profileId = getClientProfileId(selectedClient);
                
                if (!profileId) {
                    console.error('Impossible de trouver l\'ID du profil client:', selectedClient);
                    toast({
                        title: "Erreur",
                        description: "Impossible d'identifier le client.",
                        variant: "destructive"
                    });
                    return;
                }

                const response = await adminAPI.getClientById(profileId);
                
                // Vérifier que la réponse contient bien les données
                if (!response.data) {
                    console.error('Réponse API invalide:', response);
                    toast({
                        title: "Erreur",
                        description: "Les données reçues sont invalides.",
                        variant: "destructive"
                    });
                    return;
                }

                // Debug: afficher la structure des données
                console.log('=== STRUCTURE DES DONNÉES CLIENT ===');
                console.log('Réponse complète:', response.data);
                console.log('user:', response.data.user);
                console.log('profile:', response.data.profile);
                console.log('profile_data:', response.data.profile_data);
                console.log('Clés principales:', Object.keys(response.data));
                console.log('====================================');

                setClientDetails(response.data);
            } catch (error) {
                console.error('Erreur lors du chargement des détails:', error);
                console.error('Client sélectionné:', selectedClient);
                toast({
                    title: "Erreur",
                    description: error.response?.data?.detail || "Impossible de charger les détails du client.",
                    variant: "destructive"
                });
            } finally {
                setClientDetailsLoading(false);
            }
        };

        fetchClientDetails();
    }, [selectedClient]);

    // Filtrage des clients
    const filteredClients = clients.filter(client => {
        // Accéder aux données de manière flexible
        const user = client.user || client.user_detail || client;
        const profile = client.profile_data || client.profile || client.client_profile || {};
        
        const matchesSearch = 
            user?.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user?.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            profile?.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user?.logiconnect_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            profile?.logiconnect_id?.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesStatus = statusFilter === 'all' || user?.status === statusFilter;
        const matchesType = typeFilter === 'all' || profile?.type_client === typeFilter;
        
        return matchesSearch && matchesStatus && matchesType;
    });

    const handleSelectClient = (client) => {
        setSelectedClient(client);
        setActiveTab('details');
        // Sur mobile, masquer la liste et afficher les détails
        setShowMobileList(false);
    };

    const handleBackToList = () => {
        setShowMobileList(true);
    };

    const handleDelete = (client) => {
        setDeleteDialog({ open: true, client });
    };

    const confirmDelete = async () => {
        if (deleteDialog.client) {
            try {
                await adminAPI.deleteClient(deleteDialog.client.id);
                setClients(clients.filter(c => c.id !== deleteDialog.client.id));
                if (selectedClient?.id === deleteDialog.client.id) {
                    setSelectedClient(null);
                    setClientDetails(null);
                }
                toast({ 
                    title: "Succès", 
                    description: "Client supprimé avec succès.",
                });
            } catch (error) {
                console.error('Erreur lors de la suppression:', error);
                let errorMessage = 'Impossible de supprimer le client';
                if (error.response?.data?.detail) {
                    errorMessage = error.response.data.detail;
                }
                toast({
                    title: "Erreur",
                    description: errorMessage,
                    variant: "destructive"
                });
            }
        }
        setDeleteDialog({ open: false, client: null });
    };

    const handleRefresh = async () => {
        try {
            setLoading(true);
            const response = await adminAPI.getClients({});
            const clientsData = (response.data.results || response.data || []).map(client => ({
                ...client,
                requests_count: client.profile_data?._requests_count || client.requests_count || 0,
                completed_requests: client.profile_data?._completed_requests || 0,
                total_spent: client.profile_data?._total_spent || client.profile_data?.total_spent || 0,
                profile_data: {
                    ...client.profile_data,
                    total_spent: client.profile_data?._total_spent || client.profile_data?.total_spent || 0,
                }
            }));
            
            if (!Array.isArray(clientsData)) {
                console.error('Format de données invalide:', response.data);
                toast({
                    title: "Erreur",
                    description: "Format de données invalide reçu du serveur.",
                    variant: "destructive"
                });
                return;
            }

            setClients(clientsData);
            
            // Si un client est sélectionné, vérifier qu'il existe toujours dans la nouvelle liste
            if (selectedClient) {
                const stillExists = clientsData.find(c => c.id === selectedClient.id);
                if (!stillExists) {
                    // Le client sélectionné n'existe plus, sélectionner le premier
                    if (clientsData.length > 0) {
                        const firstClient = clientsData[0];
                        const profileId = getClientProfileId(firstClient);
                        if (profileId) {
                            setSelectedClient(firstClient);
                        } else {
                            setSelectedClient(null);
                        }
                    } else {
                        setSelectedClient(null);
                    }
                }
            }
            
            toast({ title: "Actualisé", description: "Liste des clients actualisée." });
        } catch (error) {
            console.error('Erreur lors du rafraîchissement:', error);
            toast({
                title: "Erreur",
                description: error.response?.data?.detail || "Impossible d'actualiser la liste.",
                variant: "destructive"
            });
        } finally {
            setLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        const statusMap = {
            'ACTIVE': 'bg-green-500/20 text-green-400 border-green-500/30',
            'PENDING': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
            'SUSPENDED': 'bg-red-500/20 text-red-400 border-red-500/30',
            'BANNED': 'bg-red-500/20 text-red-400 border-red-500/30',
        };
        const textMap = {
            'ACTIVE': 'Actif',
            'PENDING': 'En attente',
            'SUSPENDED': 'Suspendu',
            'BANNED': 'Banni',
        };
        return <Badge className={`text-xs ${statusMap[status] || 'bg-gray-500/20 text-gray-400 border-gray-500/30'}`}>{textMap[status] || status}</Badge>;
    };

    const getTypeBadge = (type) => {
        const typeMap = {
            'INDIVIDUAL': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
            'BUSINESS': 'bg-purple-500/20 text-purple-400 border-purple-500/30',
            'NGO': 'bg-green-500/20 text-green-400 border-green-500/30',
            'MINING': 'bg-orange-500/20 text-orange-400 border-orange-500/30',
        };
        const textMap = {
            'INDIVIDUAL': 'Particulier',
            'BUSINESS': 'Entreprise',
            'NGO': 'ONG',
            'MINING': 'Mining',
        };
        return <Badge className={`text-xs ${typeMap[type] || 'bg-gray-500/20 text-gray-400 border-gray-500/30'}`}>{textMap[type] || type}</Badge>;
    };

    const formatCurrency = (amount) => {
        if (!amount && amount !== 0) return '0 GNF';
        return new Intl.NumberFormat('fr-FR').format(amount) + ' GNF';
    };

    return (
        <>
            <Helmet>
                <title>Gestion des Clients - LogiConnect</title>
            </Helmet>
            
            <DashboardLayout sidebarLinks={adminSidebarLinks}>
                <div className="flex h-[calc(100vh-4rem)] md:h-[calc(100vh-4rem)] min-h-0 gap-4">
                    {/* Sidebar gauche - Liste des clients */}
                    {/* Sur mobile : afficher uniquement si showMobileList est true */}
                    <div className={`
                        ${showMobileList ? 'flex' : 'hidden md:flex'}
                        w-full md:w-80 
                        border-r border-border bg-card 
                        flex-col
                    `}>
                        {/* Header avec recherche et filtres */}
                        <div className="p-4 border-b border-border space-y-4">
                            <div className="flex items-center justify-between">
                                <h2 className="text-lg font-semibold">Clients</h2>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={handleRefresh}
                                    disabled={loading}
                                >
                                    <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                                </Button>
                            </div>
                            
                            {/* Recherche */}
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
                                <Input
                                    placeholder="Rechercher..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-10"
                                />
                            </div>

                            {/* Filtres */}
                            <div className="flex gap-2">
                                <Select value={statusFilter} onValueChange={setStatusFilter}>
                                    <SelectTrigger className="flex-1">
                                        <SelectValue placeholder="Statut" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Tous</SelectItem>
                                        <SelectItem value="ACTIVE">Actif</SelectItem>
                                        <SelectItem value="PENDING">En attente</SelectItem>
                                        <SelectItem value="SUSPENDED">Suspendu</SelectItem>
                                    </SelectContent>
                                </Select>
                                
                                <Select value={typeFilter} onValueChange={setTypeFilter}>
                                    <SelectTrigger className="flex-1">
                                        <SelectValue placeholder="Type" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Tous</SelectItem>
                                        <SelectItem value="INDIVIDUAL">Particulier</SelectItem>
                                        <SelectItem value="BUSINESS">Entreprise</SelectItem>
                                        <SelectItem value="NGO">ONG</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        {/* Liste des clients */}
                        <div className="flex-1 overflow-y-auto">
                            {loading ? (
                                <div className="flex items-center justify-center py-12">
                                    <Loader2 className="w-6 h-6 animate-spin text-primary" />
                                </div>
                            ) : filteredClients.length === 0 ? (
                                <div className="p-4 text-center text-muted-foreground text-sm">
                                    Aucun client trouvé
                                </div>
                            ) : (
                                <div className="p-2 space-y-1">
                                    {filteredClients.map((client) => {
                                        // Accéder aux données de manière flexible
                                        const user = client.user || client.user_detail || client;
                                        const profile = client.profile_data || client.profile || client.client_profile || {};
                                        const isSelected = selectedClient?.id === client.id;
                                        
                                        return (
                                            <div
                                                key={client.id}
                                                onClick={() => handleSelectClient(client)}
                                                className={`
                                                    p-3 rounded-lg cursor-pointer transition-all
                                                    ${isSelected 
                                                        ? 'bg-primary/10 border-2 border-primary' 
                                                        : 'bg-card border border-border hover:bg-muted/50'
                                                    }
                                                `}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <Avatar className="h-10 w-10">
                                                        <AvatarImage 
                                                            src={user?.photo || `https://api.dicebear.com/7.x/initials/svg?seed=${user?.first_name}+${user?.last_name}`} 
                                                            alt={`${user?.first_name} ${user?.last_name}`} 
                                                        />
                                                        <AvatarFallback className="text-xs">
                                                            {user?.first_name?.[0] || 'C'}{user?.last_name?.[0] || 'C'}
                                                        </AvatarFallback>
                                                    </Avatar>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2 mb-1">
                                                            <p className="font-semibold text-sm truncate">
                                                                {profile?.company_name || `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || 'Client'}
                                                            </p>
                                                            {isSelected && (
                                                                <ChevronRight className="w-4 h-4 text-primary flex-shrink-0" />
                                                            )}
                                                        </div>
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            {getStatusBadge(user?.status)}
                                                            {profile?.type_client && getTypeBadge(profile.type_client)}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Side droite - Détails du client sélectionné */}
                    {/* Sur mobile : afficher uniquement si showMobileList est false */}
                    <div className={`
                        ${showMobileList ? 'hidden' : 'flex'}
                        md:flex
                        flex-1 flex flex-col overflow-y-auto
                    `}>
                        {!selectedClient ? (
                            <div className="flex items-center justify-center h-full text-muted-foreground p-4">
                                <div className="text-center">
                                    <Users className="w-12 h-12 md:w-16 md:h-16 mx-auto mb-4 opacity-50" />
                                    <p className="text-sm md:text-lg">Sélectionnez un client pour voir ses détails</p>
                                </div>
                            </div>
                        ) : clientDetailsLoading ? (
                            <div className="flex items-center justify-center h-full">
                                <Loader2 className="w-8 h-8 animate-spin text-primary" />
                            </div>
                        ) : clientDetails ? (
                            <>
                                {/* En-tête avec informations du client */}
                                <div className="p-4 md:p-6 border-b border-border bg-card">
                                    {/* Bouton retour sur mobile */}
                                    <div className="md:hidden mb-4">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={handleBackToList}
                                            className="mb-2"
                                        >
                                            <ArrowLeft className="w-4 h-4 mr-2" />
                                            Retour à la liste
                                        </Button>
                                    </div>
                                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
                                        <div className="flex items-center gap-3 md:gap-4 min-w-0 flex-1">
                                            {(() => {
                                                const user = getUserData(clientDetails);
                                                const profile = getProfileData(clientDetails);
                                                return (
                                                    <>
                                                        <Avatar className="w-12 h-12 md:w-16 md:h-16 flex-shrink-0">
                                                            <AvatarImage 
                                                                src={user?.photo || `https://api.dicebear.com/7.x/initials/svg?seed=${user?.first_name}+${user?.last_name}`} 
                                                                alt={`${user?.first_name} ${user?.last_name}`} 
                                                            />
                                                            <AvatarFallback>
                                                                {user?.first_name?.[0] || 'C'}{user?.last_name?.[0] || 'C'}
                                                            </AvatarFallback>
                                                        </Avatar>
                                                        <div className="min-w-0 flex-1">
                                                            <div className="flex flex-wrap items-center gap-2 md:gap-3 mb-2">
                                                                <h1 className="text-lg md:text-2xl font-bold break-words">
                                                                    {profile?.company_name || 
                                                                     `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || 'Client'}
                                                                </h1>
                                                                {getStatusBadge(user?.status)}
                                                                {profile?.type_client && getTypeBadge(profile.type_client)}
                                                            </div>
                                                            {profile?.company_name && (
                                                                <p className="text-muted-foreground">
                                                                    {user?.first_name} {user?.last_name}
                                                                </p>
                                                            )}
                                                        </div>
                                                    </>
                                                );
                                            })()}
                                        </div>
                                        <div className="flex items-center gap-2 w-full sm:w-auto">
                                            <Button variant="outline" size="sm" className="flex-1 sm:flex-initial">
                                                <Edit className="w-4 h-4 mr-2" />
                                                Modifier
                                            </Button>
                                            <Button 
                                                variant="outline" 
                                                size="sm"
                                                onClick={() => handleDelete(selectedClient)}
                                                className="text-destructive hover:text-destructive flex-1 sm:flex-initial"
                                            >
                                                <Trash2 className="w-4 h-4 mr-2" />
                                                Supprimer
                                            </Button>
                                        </div>
                                    </div>

                                    {/* Informations rapides */}
                                    {(() => {
                                        const user = getUserData(clientDetails);
                                        const profile = getProfileData(clientDetails);
                                        return (
                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4">
                                                <div>
                                                    <Label className="text-xs text-muted-foreground">Email</Label>
                                                    <p className="text-sm font-medium">{user?.email || 'N/A'}</p>
                                                </div>
                                                <div>
                                                    <Label className="text-xs text-muted-foreground">Téléphone</Label>
                                                    <p className="text-sm font-medium">{user?.phone_number || 'N/A'}</p>
                                                </div>
                                                <div>
                                                    <Label className="text-xs text-muted-foreground">Demandes</Label>
                                                    <p className="text-sm font-medium">{profile?.requests_count || clientDetails?.requests_count || 0}</p>
                                                </div>
                                                <div>
                                                    <Label className="text-xs text-muted-foreground">Total dépensé</Label>
                                                    <p className="text-sm font-bold text-yellow-400">{formatCurrency(profile?.total_spent || clientDetails?.total_spent || 0)}</p>
                                                </div>
                                            </div>
                                        );
                                    })()}
                                </div>

                                {/* Onglets */}
                                <div className="flex-1">
                                    <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
                                        <div className="px-4 md:px-6 pt-4 border-b border-border overflow-x-auto flex-shrink-0">
                                            <TabsList className="w-full justify-start gap-1 h-auto p-1 bg-muted/50">
                                                <TabsTrigger 
                                                    value="details" 
                                                    className="px-4 md:px-6 py-3 text-sm md:text-base font-medium data-[state=active]:bg-yellow-400 data-[state=active]:text-black data-[state=active]:font-semibold"
                                                >
                                                    Détails
                                                </TabsTrigger>
                                                <TabsTrigger 
                                                    value="requests" 
                                                    className="px-4 md:px-6 py-3 text-sm md:text-base font-medium data-[state=active]:bg-yellow-400 data-[state=active]:text-black data-[state=active]:font-semibold"
                                                >
                                                    Demandes
                                                </TabsTrigger>
                                                <TabsTrigger 
                                                    value="bookings" 
                                                    className="px-4 md:px-6 py-3 text-sm md:text-base font-medium data-[state=active]:bg-yellow-400 data-[state=active]:text-black data-[state=active]:font-semibold"
                                                >
                                                    Missions
                                                </TabsTrigger>
                                                <TabsTrigger 
                                                    value="payments" 
                                                    className="px-4 md:px-6 py-3 text-sm md:text-base font-medium data-[state=active]:bg-yellow-400 data-[state=active]:text-black data-[state=active]:font-semibold"
                                                >
                                                    Paiements
                                                </TabsTrigger>
                                                <TabsTrigger 
                                                    value="ratings" 
                                                    className="px-4 md:px-6 py-3 text-sm md:text-base font-medium data-[state=active]:bg-yellow-400 data-[state=active]:text-black data-[state=active]:font-semibold"
                                                >
                                                    Avis
                                                </TabsTrigger>
                                            </TabsList>
                                        </div>

                                        <div className="flex-1 p-4 md:p-6">
                                            <TabsContent value="details" className="mt-0">
                                                {(() => {
                                                    const user = getUserData(clientDetails);
                                                    const profile = getProfileData(clientDetails);
                                                    return (
                                                        <div className="space-y-6">
                                                            {/* Informations générales */}
                                                            <Card>
                                                                <CardContent className="p-6">
                                                                    <h3 className="text-lg font-semibold mb-4">Informations Générales</h3>
                                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                                        <div>
                                                                            <Label className="text-muted-foreground text-sm">Type de client</Label>
                                                                            <p className="font-medium">
                                                                                {profile?.type_client === 'INDIVIDUAL' ? 'Particulier' :
                                                                                 profile?.type_client === 'BUSINESS' ? 'Entreprise' :
                                                                                 profile?.type_client === 'NGO' ? 'ONG' :
                                                                                 profile?.type_client === 'MINING' ? 'Mining' :
                                                                                 'N/A'}
                                                                            </p>
                                                                        </div>
                                                                        <div>
                                                                            <Label className="text-muted-foreground text-sm">Email</Label>
                                                                            <p className="font-medium">{user?.email || 'N/A'}</p>
                                                                        </div>
                                                                        <div>
                                                                            <Label className="text-muted-foreground text-sm">Téléphone</Label>
                                                                            <p className="font-medium">{user?.phone_number || 'N/A'}</p>
                                                                        </div>
                                                                        <div>
                                                                            <Label className="text-muted-foreground text-sm">Membre depuis</Label>
                                                                            <p className="font-medium">
                                                                                {user?.date_joined 
                                                                                    ? new Date(user.date_joined).toLocaleDateString('fr-FR')
                                                                                    : 'N/A'}
                                                                            </p>
                                                                        </div>
                                                                        {profile?.company_name && (
                                                                            <div>
                                                                                <Label className="text-muted-foreground text-sm">Nom de l'entreprise</Label>
                                                                                <p className="font-medium">{profile.company_name}</p>
                                                                            </div>
                                                                        )}
                                                                        {profile?.address && (
                                                                            <div>
                                                                                <Label className="text-muted-foreground text-sm">Adresse</Label>
                                                                                <p className="font-medium">{profile.address}</p>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </CardContent>
                                                            </Card>

                                                            {/* Statistiques */}
                                                            <Card>
                                                                <CardContent className="p-6">
                                                                    <h3 className="text-lg font-semibold mb-4">Statistiques</h3>
                                                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                                                        <div>
                                                                            <Label className="text-muted-foreground text-sm">Demandes</Label>
                                                                            <p className="font-bold text-lg">{profile?.requests_count || clientDetails?.requests_count || 0}</p>
                                                                        </div>
                                                                        <div>
                                                                            <Label className="text-muted-foreground text-sm">Missions terminées</Label>
                                                                            <p className="font-bold text-lg">{profile?.completed_requests || clientDetails?.completed_requests || 0}</p>
                                                                        </div>
                                                                        <div>
                                                                            <Label className="text-muted-foreground text-sm">Note moyenne</Label>
                                                                            <div className="flex items-center gap-1 mt-1">
                                                                                <Star className="w-4 h-4 text-yellow-400" />
                                                                                <p className="font-bold text-lg">{profile?.rating_avg || '0'}</p>
                                                                            </div>
                                                                        </div>
                                                                        <div>
                                                                            <Label className="text-muted-foreground text-sm">Total dépensé</Label>
                                                                            <p className="font-bold text-lg text-yellow-400">{formatCurrency(profile?.total_spent || clientDetails?.total_spent || 0)}</p>
                                                                        </div>
                                                                    </div>
                                                                </CardContent>
                                                            </Card>
                                                        </div>
                                                    );
                                                })()}
                                            </TabsContent>

                                            <TabsContent value="requests" className="mt-0">
                                                {selectedClient && getClientProfileId(selectedClient) && (
                                                    <RequestManagement 
                                                        clientId={getClientProfileId(selectedClient)}
                                                        onStatisticsUpdate={() => {}}
                                                    />
                                                )}
                                            </TabsContent>

                                            <TabsContent value="bookings" className="mt-0">
                                                {selectedClient && getClientProfileId(selectedClient) && (
                                                    <BookingManagement 
                                                        clientId={getClientProfileId(selectedClient)}
                                                        onStatisticsUpdate={() => {}}
                                                    />
                                                )}
                                            </TabsContent>

                                            <TabsContent value="payments" className="mt-0">
                                                {selectedClient && getClientProfileId(selectedClient) && (
                                                    <PaymentManagement 
                                                        clientId={getClientProfileId(selectedClient)}
                                                    />
                                                )}
                                            </TabsContent>

                                            <TabsContent value="ratings" className="mt-0">
                                                {selectedClient && getClientProfileId(selectedClient) && (
                                                    <RatingManagement 
                                                        clientId={getClientProfileId(selectedClient)}
                                                    />
                                                )}
                                            </TabsContent>
                                        </div>
                                    </Tabs>
                                </div>
                            </>
                        ) : null}
                    </div>
                </div>

                {/* Dialog de confirmation de suppression */}
                <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, client: null })}>
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>Êtes-vous absolument sûr ?</AlertDialogTitle>
                            <AlertDialogDescription>
                                Cette action est irréversible. Le client sera définitivement supprimé 
                                ainsi que toutes ses données associées.
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel>Annuler</AlertDialogCancel>
                            <AlertDialogAction 
                                onClick={confirmDelete}
                                className="bg-red-600 hover:bg-red-700"
                            >
                                Supprimer
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </DashboardLayout>
        </>
    );
};

export default AdminClients;
